import { CaretDownIcon, CaretRightIcon, XIcon } from "@phosphor-icons/react";
import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { KIT_LABELS } from "../labels.ts";
import { StatusIcon } from "./status.tsx";
import type { StatusKind } from "./status-kinds.ts";

// Dense operational lists (docs/design-system.md §0.3): single-line rows at
// --z-row-h, grouped by state. The full content is in the detail.

interface RowContent {
  /** A short reference such as ZOO-31, in monospace before the status. */
  code?: string;
  status?: StatusKind;
  progress?: number | null;
  /** The type icon when the row has no status. */
  icon?: ReactNode;
  title: string;
  /** A second, quieter line of text after the title (wraps under it on narrow screens). */
  sub?: ReactNode;
  /** Short properties as chips; they give way on narrow screens and beside a detail. */
  chips?: ReactNode;
  /** Who does it, as an `Executor`. */
  executor?: ReactNode;
  /** The next time (due, matures), right-aligned. */
  meta?: ReactNode;
  /** Controls at the end of the row, such as one small button. */
  end?: ReactNode;
  /** The label of an item not opened yet ("New"); it shows as a dot. */
  unread?: string;
}

function Content(p: RowContent) {
  return <>
    {p.unread !== undefined && <span className="z-lrow-unread" data-unread={p.unread ? "" : undefined}>{p.unread && <span className="z-sr">{p.unread}</span>}</span>}
    {p.code && <span className="z-lrow-code">{p.code}</span>}
    {p.status ? <StatusIcon kind={p.status} progress={p.progress} /> : p.icon && <span className="z-lrow-icon" aria-hidden>{p.icon}</span>}
    <span className="z-lrow-text"><span className="z-lrow-title">{p.title}</span>{p.sub && <span className="z-lrow-sub">{p.sub}</span>}</span>
    {p.chips && <span className="z-lrow-chips">{p.chips}</span>}
    {p.executor && <span className="z-lrow-exec">{p.executor}</span>}
    {p.meta && <span className="z-lrow-meta">{p.meta}</span>}
    {p.end && <span className="z-lrow-end">{p.end}</span>}
  </>;
}

/**
 * One row of an operational list. With `to` it is a link; with `onOpen` it is
 * an option of a listbox the parent owns (J/K move the selection, Enter opens);
 * otherwise a plain row.
 */
export function ListRow(props: RowContent & {
  to?: string; params?: Record<string, string>; search?: Record<string, string>;
  id?: string; selected?: boolean; onSelect?: () => void; onOpen?: () => void; rowRef?: (node: HTMLLIElement | null) => void;
}) {
  const { to, params, search, id, selected, onSelect, onOpen, rowRef, ...content } = props;
  if (to) return <Link to={to} params={params} search={search} className="z-lrow" aria-current={selected ? "true" : undefined}><Content {...content} /></Link>;
  if (onOpen) return <li id={id} role="option" aria-selected={Boolean(selected)} tabIndex={selected ? 0 : -1} className="z-lrow" ref={rowRef}
    onFocus={onSelect} onClick={() => { onSelect?.(); onOpen(); }}><Content {...content} /></li>;
  return <div className="z-lrow"><Content {...content} /></div>;
}

/** A group of rows by state; collapsible when `onToggle` is given. */
export function GroupHeader({ label, count, status, progress, note, collapsed, onToggle, children }: {
  label: string; count?: number | string; status?: StatusKind; progress?: number | null; note?: ReactNode; collapsed?: boolean; onToggle?: () => void; children?: ReactNode;
}) {
  const caret = collapsed ? <CaretRightIcon size={16} weight="bold" aria-hidden /> : <CaretDownIcon size={16} weight="bold" aria-hidden />;
  const head = <>
    {onToggle && <span className="z-group-caret">{caret}</span>}
    {status && <StatusIcon kind={status} progress={progress} label="" />}
    <span className="z-group-label">{label}</span>
    {count !== undefined && <span className="z-group-count">{count}</span>}
  </>;
  return <div className="z-group">
    {onToggle ? <button type="button" className="z-group-toggle" aria-expanded={!collapsed} onClick={onToggle}>{head}</button> : <span className="z-group-toggle">{head}</span>}
    {note && <span className="z-group-note">{note}</span>}
    {children && <span className="z-group-end">{children}</span>}
  </div>;
}

/** An applied filter: what it filters, its value, and a way to remove it. */
export function FilterChip({ label, value, onOpen, onRemove }: { label: string; value: string; onOpen?: () => void; onRemove?: () => void }) {
  return <span className="z-filter">
    <button type="button" className="z-filter-open" onClick={onOpen} disabled={!onOpen}><span className="z-filter-label">{label}</span><span className="z-filter-value">{value}</span></button>
    {onRemove && <button type="button" className="z-filter-remove" aria-label={KIT_LABELS.removeFilter(`${label} ${value}`)} title={KIT_LABELS.removeFilter(`${label} ${value}`)} onClick={onRemove}><XIcon size={12} /></button>}
  </span>;
}
